import {AfterViewInit, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {SnackService} from '../../services/snack/snack.service';
import {TranslateService} from '@ngx-translate/core';
import {cloneDeep, find, isEqual, sortBy, uniqBy} from 'lodash-es';
import {AuthService} from '../../services/auth/auth.service';
import {
  AnalyticsTracker,
  AuditLogPage,
  BankAccount,
  BankAccountStatus,
  CommissionLevel,
  ContactPerson,
  InviteStatus,
  InvoicingPeriod,
  LegalPerson,
  LegalPersonPost,
  PaymentLink,
  Promoter,
  Representative,
  RepresentativeInvite,
  RepresentativeInvitePost,
  RoleSimple,
  Salespoint,
  SalespointRole,
  SalespointWithAlias,
  ShopProvider,
  SortOrderEnum,
  User,
  UserStatus
} from '../../generated/sales-terminal-client';
import {LegalPersonService} from '../../services/legalPerson/legalPerson.service';
import {MatDialog} from '@angular/material/dialog';
import {RouteService} from '../../services/route/route.service';
import {PromoterService} from '../../services/promoter/promoter.service';
import {ShopProviderService} from '../../services/shopprovider/shop-provider.service';
import {ScrollLoad} from '../scroll-load-component/ScrollLoad';
import {UtilService} from '../../services/util/util.service';
import {SalespointsService} from '../../services/salespoints/salespoints.service';
import {UserGet, UserService} from '../../services/user/user.service';
import {Observable, ReplaySubject, Subscription, tap} from 'rxjs';
import {PendingChangesComponent} from '../../guards/pendingChanges/pending-changes-component';
import {LanguageService} from '../../services/language/language.service';
import {TranslationService} from '../../generated/translation-client';
import {ValidationService} from '../../services/validation/validation.service';
import {CountryService, TranslatedCountry} from '../../services/country/country.service';
import {ConfirmDialogComponent} from '../confirm-dialog/confirm-dialog.component';
import {PendingChangesDialogComponent} from '../pending-changes-dialog/pending-changes-dialog.component';
import {NavigationEnd, Router} from '@angular/router';
import {CONTINUE_WITHOUT_SAVING} from '../../modules/sales-terminal/sales-terminal.constants';
import {Privileges} from '../../modules/admin/privileges/privileges.constants';
import {finalize, switchMap} from 'rxjs/operators';
import {MatTabChangeEvent, MatTabGroup} from '@angular/material/tabs';
import {EVENT_TITLE_MAX_LENGTH} from '../../services/self-service/event/self-service-event-base.service';
import {TextInputType} from '../forms/text-input/text-input.component';
import {NumericInputType} from '../forms/numeric-input/numeric-input.component';
import {ViewService} from '../../services/view/view.service';
import {S3UploaderRatios, S3UploaderTypes} from '../s3/uploader/s3-uploader.types';
import {StringUtilService} from "../../services/util/string-util.service";

export interface LegalPersonContentDialog {
  template: TemplateRef<any>;
  callback?: Function;
  isValid?: Function;
  title: string;
  titleTooltip?: string;
  saveButton?: string;
  model: any;
  index?: number;
  width?: string;
  primaryButton?: string;
  primaryAction?: Function;
  primaryButtonDisabled?: Function;
  secondaryButton?: string;
  secondaryAction?: Function;
  secondaryButtonDisabled?: Function;
  removeAction?: Function;
  validations?: any;
  isLoading?: Function;
  additionalAction?: Function;
  mode?: 'ADD' | 'UPDATE';
  nameMaxLength?: number;
}

enum DialogAction {
  CLAIMING = 'claiming',
  REMOVE = 'remove',
  ADD_MANUALLY = 'add_manually'
}

export interface AnalyticsTrackerWrapper extends AnalyticsTracker {
  translationKey?: string;
}

export const VERIFIED_TAB = 'verified';
export const INVITES_TAB = 'invites';
export const REPRESENTATIVE_TABS = [VERIFIED_TAB, INVITES_TAB] as const;
export type RepresentativeTab = typeof REPRESENTATIVE_TABS[number];
export const REPRESENTATIVE_TAB_MAP = {
  VERIFIED: VERIFIED_TAB,
  INVITES: INVITES_TAB
} as const;
export const CONTACT_PERSON_NAME_MAX_LENGTH: number = 300;

@Component({
  selector: 'app-legal-person-view',
  templateUrl: './legal-person-view.component.html',
  styleUrls: ['./legal-person-view.component.scss']
})
export class LegalPersonViewComponent implements OnInit, AfterViewInit, OnDestroy, PendingChangesComponent {
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return !this.pendingChanges && !this.invoicingChanged || !this.isModelChanged;
  }
  @ViewChild('bankAccountTabs')
  bankAccountTabs: MatTabGroup;
  @ViewChild('modalTemplate')
  modalTemplate: TemplateRef<any>;
  @ViewChild('contactPersonModalContent')
  contactPersonModalContent: TemplateRef<any>;
  @ViewChild('representativesModalContent')
  representativesModalContent: TemplateRef<any>;
  @ViewChild('billingPaymentLinkModalContent')
  billingPaymentLinkModalContent: TemplateRef<any>;
  @ViewChild('billingAddManuallyModalContent')
  billingAddManuallyModalContent: TemplateRef<any>;
  @ViewChild('analyticsTrackersTemplateModalContent')
  analyticsTrackersTemplateModalContent: TemplateRef<any>;
  @ViewChild('shopProviderModalContent')
  shopProviderModalContent: TemplateRef<any>;
  @Input()
  withoutFooter: boolean = false;
  @Input()
  isInternalComponent: boolean;
  @Input()
  legalPersonId: number;
  @Input()
  readonly: boolean;

  @Output()
  confirmChange: EventEmitter<LegalPerson> = new EventEmitter<LegalPerson>();
  @Output()
  cancelChange: EventEmitter<any> = new EventEmitter();

  public static ANALYTICS_TRACKER_TYPES: AnalyticsTracker.TypeEnum[] = Object.values(AnalyticsTracker.TypeEnum);
  public static BELARUS_SUPER_CENTRE_ID: number = 1873;
  public COMPANY_REG_NO_MAX_LENGTH = 60;
  public ANALYTICS_TRACKER_MAX_LENGTH = 100;
  public AUDIT_LOG_TAB_ARIA_LABEL: string = 'audit_log_tab';

  public legalPerson: LegalPerson = null;
  public legalPersonOrig: LegalPerson = null;
  public promoters: Promoter[] = [];
  public legalPersonPromoterIds: number[] = [];
  public promoterScrollLoadList: ScrollLoad<Promoter>[] = [];
  public shopProviders: ShopProvider[] = [];
  public legalPersonShopProviders: ShopProvider[] = [];
  public shopProviderScrollLoadList: ScrollLoad<ShopProvider>[] = [];
  public salespoints: SalespointWithAlias[] = [];
  public salespointScrollLoadList: ScrollLoad<SalespointWithAlias>[] = [];
  public legalRoles: SalespointRole[] = [];
  public invoicingPeriods: InvoicingPeriod[] = Object.values(InvoicingPeriod);
  public isLiableForVat: boolean;
  public isExemptFromVat: boolean;
  public isBusinessAccount: boolean = false;
  public contactAddressIsDifferent: boolean;
  public agreedWithDocument: boolean;
  public projectManagers: User[] = [];
  public projectManagersFiltered: ReplaySubject<User[]> = new ReplaySubject<User[]>();
  public analyticsTrackersTypesFiltered: ReplaySubject<AnalyticsTrackerWrapper[]> = new ReplaySubject<AnalyticsTrackerWrapper[]>();
  public isLoading: boolean;
  public inviteStatus = InviteStatus;
  private _subscriptions: Subscription[] = [];
  public canChange: boolean;
  public canChangeAll: boolean;
  public canManageRepresentative: boolean;
  public canAddBankAccountManually: boolean;
  public editSalespoints: Record<string, boolean> = { null: true };
  public editPromoters: Record<string, boolean> = { null: true };
  public editShopProviders: Record<string, boolean> = { null: true };
  public representativeTabMap = REPRESENTATIVE_TAB_MAP;
  public representativeTabsList = REPRESENTATIVE_TABS;
  public representativeTab: RepresentativeTab = REPRESENTATIVE_TAB_MAP.VERIFIED;
  public representativeTabIndex: number = 0;
  public Privileges = Privileges;
  private _errors: any = {};
  public expanded: any = {};
  public buttonOk: string;
  public auditLogPage: AuditLogPage;
  public auditPageNumber: number = 1;
  private auditPageSize: number = 30;
  private projectManagersPageSize: number = 100;
  public countriesFiltered: ReplaySubject<TranslatedCountry[]> = new ReplaySubject<TranslatedCountry[]>();

  public BankAccountStatus = BankAccountStatus;
  public BankAccountStatuses: BankAccountStatus[] = this.utilService.arrayMove(Object.values(BankAccountStatus), 1, 0);
  public bankAccountStatusTab: number = 0;
  public billingBankAccounts: BankAccount[] = [];
  public isBankAccountPending: boolean = false;
  public generatePaymentLink: boolean = false;
  public viewTab: number = 0;
  public isPhoneNrValid = true;
  private markForTabCheck: boolean;
  private queryParams: any = {};

  public pendingChanges: boolean;
  public invoicingChanged: boolean;
  public isUserRepresentative: boolean;

  constructor(public utilService: UtilService,
              public routeService: RouteService,
              public authService: AuthService,
              public legalPersonService: LegalPersonService,
              public promoterService: PromoterService,
              public shopProviderService: ShopProviderService,
              public salespointsService: SalespointsService,
              public userService: UserService,
              private snackService: SnackService,
              private translateService: TranslateService,
              private translationService: TranslationService,
              private languageService: LanguageService,
              public countryService: CountryService,
              private dialog: MatDialog,
              private router: Router,
              public viewService: ViewService,
              private stringUtilService: StringUtilService) {
    this._subscriptions.push(this.authService.pendingChangeListener.subscribe(() =>
      this.authService.pendingChangesActive = this.pendingChanges || this.invoicingChanged || this.isModelChanged));
  }

  ngOnInit(): void {
    this.preparePage();
    if (this.utilService.translationsLoaded) {
      this.setupPendingChangesTranslations();
    } else {
      this._subscriptions.push(this.utilService.translationsLoadedChange.subscribe((value) => {
        if (value) {
          this.setupPendingChangesTranslations();
        }
      }));
    }
    this._subscriptions.push(this.routeService.queryParams.subscribe((params) => {
      if (params.tab) {
        if (params.tab === 'billing') {
          this.markForTabCheck = true;
          this.queryParams = params;
          this.switchToBilling();
        }
      }
    }));
    setTimeout(() => {
      if (!this.queryParams.skipNotification) {
        if (this.routeService.params['id'] === this.authService.user?.legalPersonId?.toString()) this.authService.showBankVerificationNotification(true);
      }
    }, 500);
  }
  ngAfterViewInit(): void {
    this._subscriptions.push(this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        if (this.routeService.checkRoute(['admin', 'legal-persons'], e.urlAfterRedirects)) {
          const prevId = this.routeService.prevUrl.split('view/')[1];
          if (this.routeService.url === this.routeService.prevUrl || parseInt(prevId) > 0 && this.routeService.params['id'] !== prevId) {
            this.preparePage();
          }
        }
      }
    }));
  }
  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
    this.snackService.clearFixedSnackMessage();
  }

  updateByTab(event: MatTabChangeEvent): void {
    if (this.bankAccountTabs) {
      setTimeout(() => {
        this.bankAccountTabs.realignInkBar();
      }, 100);
    }
    if (event.tab.ariaLabel === this.AUDIT_LOG_TAB_ARIA_LABEL) {
      this.getAuditLogs();
    }
  }

  switchTabsFromQuery(): void {
    if (this.markForTabCheck) {
      this.markForTabCheck = false;
      this.switchToBilling();
    }
  }

  private switchToBilling(): void {
    let tab = 0;
    if (this.utilService.isMobile) {
      tab++;
    }
    if (this.isAnyConnectedPersons) {
      tab++;
    }
    this.viewTab = tab;
    if (this.queryParams.child) {
      this.bankAccountStatusTab = this.BankAccountStatuses.indexOf(this.queryParams.child);
    }
  }

  setupPendingChangesTranslations(): void {
    if (this.isCreate) {
      this.buttonOk = this.translateService.instant('general.api_legal_persons_form_create');
    }
    if (this.isRegister) {
      this.buttonOk = this.translateService.instant('general.api_legal_persons_form_register');
    }
  }

  preparePage(): void {
    this.legalPerson = {
      promoters: [],
      shopProviders: [],
      salespoints: [],
      contactPersons: [],
      analyticsTrackers: [],
      address: {},
      contactAddress: {}
    } as LegalPerson;
    this.syncModels();
    if (this.isView) {
      this.getLegalPerson(this.legalPersonId || this.routeService.params['id']);
    } else {
      this.updatePermissions();
    }
    if (!this.isRegister && this.authService.hasProjectManagersAccessPrivileges) {
      this.populateProjectManagers();
    }
    if (this.isRegister) {
      this.translationService.getTranslationLanguages().subscribe((languages: any) =>
        this.languageService.languages = languages);
    }
    if (this.isCreate) {
      this.getSalespoints();
    }
    if (this.isInternalComponent) {
      this.setIsBusinessAccount();
    }
  }

  updatePermissions(): void {
    this.canChangeAll = this.authService.checkPrivileges(Privileges.LEGAL_PERSON_ADMIN);
    this.canChange = (!this.isReadonly && ((this.isRegister || this.isCreate) ||
      (this.legalPerson && this.legalPerson.canChange && this.authService.hasLegalChangePrivilege))) || this.canChangeAll;
    this.canManageRepresentative = this.canChange && this.authService.checkPrivileges([Privileges.LEGAL_PERSON_REPRESENTATIVES_MANAGE, Privileges.USER_GET], true);
    this.canAddBankAccountManually = this.authService.checkPrivileges(Privileges.LEGAL_PERSON_ADMIN);
  }

  get billingStatus(): boolean {
    return this.BankAccountStatuses[this.bankAccountStatusTab] === BankAccountStatus.Invalid || this.BankAccountStatuses[this.bankAccountStatusTab] === BankAccountStatus.Pending;
  }

  get isCreate(): boolean {
    return this.routeService.checkRoute(['admin', 'legal-persons', 'create']) || !!(this.isInternalComponent && !this.legalPersonId);
  }

  get isView(): boolean {
    return this.routeService.checkRoute(['admin', 'legal-persons', 'view']) || !!(this.isInternalComponent && this.legalPersonId);
  }

  get isRegister(): boolean {
    return this.routeService.checkRoute(['registration', 'legal-person'], this.routeService.url, 1);
  }

  get isReadonly(): boolean {
    if (this.isInternalComponent) {
      return this.readonly;
    }
    return false;
  }

  get isValidEmail(): boolean {
    return ((this.isCreate || this.isRegister) && this.legalPerson.email === undefined) // initial value needs to be valid for create but invalid for edit
      || (!!this.legalPerson.email && ValidationService.validateEmail(this.legalPerson.email));
  }

  get isValidVat(): boolean {
    return !this.isLiableForVat || (this.legalPerson.vatCode && this.legalPerson.vatPercent && ValidationService.validatePercentage(this.legalPerson.vatPercent, true));
  }

  get isValidVatExemptionCode(): boolean {
    return !this.isExemptFromVat || (this.legalPerson.vatExemptionCode && !this.isVatExemptCodeInvalid(this.legalPerson.vatExemptionCode));
  }

  get isRegNumberExist(): boolean {
    return this._errors?.regNumberExist === this.legalPerson.regNumber;
  }

  get isRegNumberTooLong(): boolean {
    return this.legalPerson?.regNumber?.length > this.COMPANY_REG_NO_MAX_LENGTH;
  }

  get isRegNumberValid(): boolean {
    return !this.isRegNumberExist && !this.isRegNumberTooLong;
  }

  get isValid(): boolean {
    return !!(this.legalPersonService.validateLegalPerson(this.legalPerson, this.contactAddressIsDifferent) && this.isValidVat &&
      this.isValidVatExemptionCode && this.isRegNumberValid && this.isValidEmail && this.isPhoneNrValid);
  }

  get salespointsIds(): number[] {
    return this.salespoints.map(s => s.id);
  }

  get isModelChanged(): boolean {
    return !isEqual(this.legalPerson, this.legalPersonOrig);
  }

  get isSalespointsChanged(): boolean {
    return !isEqual(this.legalPerson.salespoints, this.legalPersonOrig.salespoints);
  }

  get isAnyConnectedPersons(): boolean {
    return this.canChange || this.canManageRepresentative || !!this.legalPerson?.contactPersons?.length || !!this.legalPerson.representatives?.length || !!this.legalPerson?.representativeInvites?.length;
  }

  get canManageAnalyticsTrackers(): boolean {
    return this.canChange && !this.isCreate;
  }

  get canSetOwnerCentreSettings(): boolean {
    return this.canChange && this.authService.checkPrivileges([Privileges.LEGAL_PERSON_CHANGE, Privileges.LEGAL_PERSON_ADMIN]) &&
      (this.authService.user.isSuperCentre || this.isUserRepresentative);
  }

  get isAnyRepresentativeHasMultipleSalespoints(): boolean {
    return this.legalPerson.salespoints?.length > 1 ||
      !!this.legalPerson.representatives.filter(representative => (representative.salespointRoles?.length > 1)).length ||
      !!this.legalPerson.representativeInvites.filter(invite => (invite.salespointRoles?.length > 1)).length;
  }

  get billingBankAccountsByTab(): BankAccount[] {
    return this.billingBankAccounts.filter(item => item.status === this.BankAccountStatuses[this.bankAccountStatusTab]);
  }

  get getNotUsedAnalyticsTrackerTypes(): AnalyticsTrackerWrapper[] {
    return LegalPersonViewComponent.ANALYTICS_TRACKER_TYPES
      .filter(type => !this.legalPerson?.analyticsTrackers?.some(at => at.type === type))
      .map(type => ({ type: type, translationKey: 'general.legal_persons_analytics_tracker_type_' + type }));
  }

  sortContactPersons(): void {
    this.legalPerson.contactPersons = sortBy(this.legalPerson.contactPersons, i => { return i.firstName?.toLowerCase() + ' ' + i.lastName?.toLowerCase(); });
  }

  sortRepresentatives(): void {
    this.legalPerson.representatives = sortBy(this.legalPerson.representatives, i => { return i.firstName?.toLowerCase() + ' ' + i.lastName?.toLowerCase(); });
  }

  openDialog(options: LegalPersonContentDialog): void {
    const origModel = cloneDeep(options.model)
    const dialogRef = this.dialog.open(this.modalTemplate, {
      width: options.width || '60%',
      data: {
        template: options.template,
        title: options.title,
        titleTooltip: options.titleTooltip,
        saveButton: options.saveButton,
        model: options.model,
        index: options.index,
        isValid: options.isValid,
        validations: options.validations,
        primaryAction: options.primaryAction,
        primaryButton: options.primaryButton,
        secondaryAction: options.secondaryAction,
        secondaryButton: options.secondaryButton,
        secondaryButtonDisabled: options.secondaryButtonDisabled,
        removeAction: options.removeAction,
        isLoading: options.isLoading,
        closeModal: null,
        additionalAction: options.additionalAction,
        mode: options.mode
      },
      disableClose: true
    });
    const confirmDialog = () => {
      dialogRef._containerInstance._config.disableClose = false;
      if (dialogRef._containerInstance._config.data.isValid && !isEqual(origModel, dialogRef._containerInstance._config.data.model)) {
        const pendingChanges = new PendingChangesDialogComponent(this.dialog, this.translateService, {
          pendingChangesDescription: this.pendingChangesDescription,
          buttonOk: options.saveButton && this.translateService.instant(options.saveButton) || '',
          canNotSave: options.isValid && !options.isValid()
        });
        pendingChanges.dialogRef.afterClosed().subscribe((action) => {
          if (action === CONTINUE_WITHOUT_SAVING) {
            dialogRef.close();
          } else if (action) {
            dialogRef.close(dialogRef._containerInstance._config.data.model);
          }
        });
      } else {
        dialogRef.close();
      }
    };
    dialogRef.afterOpened().subscribe(() => {
      dialogRef._containerInstance._config.data.closeModal = (res: any) => {
        if (!res) {
          confirmDialog();
        } else {
          dialogRef._containerInstance._config.disableClose = false;
          dialogRef.close(res);
        }
      };
    });
    dialogRef.backdropClick().subscribe(() => confirmDialog());
    dialogRef.afterClosed().subscribe((res) => options.callback && options.callback(res));
  }

  prepareLegalPerson(object: LegalPerson): void {
      (object.promoters || []).forEach((promoterId, index) => {
        if (!promoterId) {
          this.legalPersonPromoterIds.splice(index, 1);
          this.promoterScrollLoadList.splice(index, 1);
        }
      });
      object.promoters = object.promoters?.filter(promoterId => promoterId);
    (object.salespoints || []).forEach((salespoint, index) => {
      if (!salespoint.id) {
        this.shopProviderScrollLoadList.splice(index, 1);
      }
    });
    object.salespoints = object.salespoints?.filter(salespoint => salespoint.id);
    object.contactPersons = object.contactPersons.filter(contact => (contact.firstName && contact.lastName));
  }

  getContactPersons(): { withId: ContactPerson[]; withoutId: ContactPerson[] } {
    const data: LegalPerson = cloneDeep(this.legalPerson);
    const contactPersonList: ContactPerson[] = [];
    (data.contactPersons || []).forEach(contactPerson => {
      const contact: ContactPerson = {
        id: contactPerson.id,
        firstName: contactPerson.firstName.trim(),
        lastName: contactPerson.lastName.trim(),
        phoneNr: contactPerson.phoneNr
      };
      contactPersonList.push(contact);
    });
    const usersWithId = uniqBy(contactPersonList.filter(cp => cp.id), 'id');
    const usersWithoutId = contactPersonList.filter(cp => !cp.id);
    return { withId: usersWithId, withoutId: usersWithoutId };
  }

  getUnsavedShopProviders(): ShopProvider[] {
    return this.legalPerson.shopProviders.filter(provider => !provider?.id && provider?.tag && provider?.name);
  }

  getData(object: LegalPerson = this.legalPerson): LegalPersonPost {
    this.prepareLegalPerson(object);
    const legalPerson: any = cloneDeep(object);
    delete legalPerson.invoicingPeriod;
    delete legalPerson.contactPersons;
    if (legalPerson.id) {
      delete legalPerson.id;
    }
    if (legalPerson.canChange) {
      delete legalPerson.canChange;
    }
    if (!this.contactAddressIsDifferent) {
      legalPerson.contactAddress = null;
    }
    if (!this.isLiableForVat) {
      delete legalPerson.vatCode;
      delete legalPerson.vatPercent;
    }
    if (!this.isExemptFromVat) {
      delete legalPerson.vatExemptionCode;
    }
    legalPerson.companyName = legalPerson.companyName.trim();
    legalPerson.salespoints = (legalPerson.salespoints || []).map((salespoint: SalespointWithAlias) => salespoint.id);
    const data: LegalPersonPost = legalPerson as LegalPersonPost;
    if (!data.promoters?.length || !data.salespoints?.length) {
      data.promoters = [];
      this.legalPersonPromoterIds = [];
    }
    return data;
  }

  getLegalPerson(legalPersonId: number): void {
    this.isLoading = true;
    this.legalPersonService.get(legalPersonId).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((legalPerson) => {
      this.legalPerson = legalPerson;
      this.postLegalPersonsUpdate();
      this.agreedWithDocument = true;
      this.isLiableForVat = !!this.legalPerson?.vatCode?.length;
      this.isExemptFromVat = !!this.legalPerson?.vatExemptionCode?.length;
      if (!this.isInternalComponent && Object.values(this.legalPerson.contactAddress).filter(v => !!v).length) {
        this.contactAddressIsDifferent = true;
      }
      this.updatePermissions();
      this.getAuditLogs();
      this.switchTabsFromQuery();
      if (!this.isInternalComponent) {
        this.getAdditionalData();
      }
      this.setIsBusinessAccount();
      this.isUserRepresentative = this.legalPerson.representatives.some(rep => rep.userId === this.authService.user.userId);
    });
  }

  setIsBusinessAccount(): void {
    this.isBusinessAccount = !this.isInternalComponent && !!this.legalPerson.salespoints.length;
  }

  getAdditionalData(): void {
    if (this.canChangeAll) {
      this.getSalespoints();
        if (this.legalPerson.promoters?.length) {
          this.getPromoters();
      }
      if (!this.shopProviders.length) {
        this.setShopProviders();
      }
    }
    this.getRoles();
    if (this.queryParams.payment_reference) { // check whether it is EveryPay callback
      this.legalPersonService.checkBankAccountStatus(this.queryParams.payment_reference, 'status_updated') // then update bank account status
        .subscribe(() => this.billingGetBankAccounts());
    } else {
      this.billingGetBankAccounts();
    }
  }

  // ROLES
  getRoles(): void {
    if (this.canManageRepresentative) {
      this.legalPersonService.getLegalPersonRolesById(this.legalPerson.id)
        .subscribe((legalRoles) => this.legalRoles = legalRoles);
    }
  }
  salespointRoles(id: number): SalespointRole[] {
    return find(this.legalRoles, { id: id })?.roles || [];
  }
  salespointRolesCompare(o1: RoleSimple, o2: RoleSimple): boolean {
    return o1?.id === o2?.id;
  }
  salespointsFiltered(salespoints: any[], salespointId: number): SalespointWithAlias[] {
    return this.legalPerson.salespoints?.filter(sp => {
      return sp.id === salespointId || !salespoints.map(s => s.id).includes(sp.id);
    });
  }
  aliasSalespoint(salespointId: number): Salespoint {
    const sp = find(this.legalPerson.salespoints || [], { id: salespointId });
    let output = null;
    if (sp && sp.alias && sp.alias.id) {
      output = sp.alias;
    }
    return output;
  }
  aliasSalespointRoles(salespoints: SalespointRole[], salespointId: number): RoleSimple[] {
    return find(salespoints, { id: salespointId })?.roles || [];
  }
  isAliasSalespoint(salespointId: number): boolean {
    return this.legalPerson.salespoints?.filter(sp => sp.alias && sp.alias.id).map(sp => sp.alias.id).includes(salespointId);
  }
  get legalHasMultipleSalespoints(): boolean {
    return this.legalPerson.salespoints?.length > 1;
  }
  get legalHasAliasSalespoints(): boolean {
    return this.legalPerson.salespoints?.filter(sp => sp.alias && sp.alias.id).length > 0;
  }

  // PROMOTERS
  getPromoters(): void {
    this.promoterService.getAll().subscribe((res) => {
      this.promoters = res;
      this.populatePromoterScrollLoadList();
    });
  }
  getPromoter(promoterId: number): Promoter {
    return this.promoters.find(promoter => promoter.id === promoterId);
  }
  populatePromoterScrollLoadList(): void {
    this.promoterScrollLoadList = [];
    this.legalPersonPromoterIds = [];
    if (!this.legalPerson.promoters) {
      this.legalPerson.promoters = [];
    }
    this.legalPerson.promoters.forEach((promoterId) => {
      const newScrollLoad = this.promoterService.getScrollLoad();
      newScrollLoad.object = this.getPromoter(promoterId);
      this.promoterScrollLoadList.push(newScrollLoad);
      this.legalPersonPromoterIds.push(promoterId);
    });
  }
  selectPromoter(index: number): void {
    this.legalPerson.promoters[index] = this.legalPersonPromoterIds[index];
    this.editPromoters[this.legalPersonPromoterIds[index]] = true;
    if (!this.promoters.length) {
      this.getPromoters();
    }
  }
  addPromoter(): void {
    this.legalPerson.promoters.unshift(null);
    this.promoterScrollLoadList.unshift(this.promoterService.getScrollLoad());
    this.legalPersonPromoterIds.unshift(null);
  }
  savePromoter(id: number): void {
    this.editPromoters[id] = false;
    this.partialUpdate('promoters',  'general.api_legal_persons_promoters_successfully_saved');
  }
  removePromoter(index: number): void {
    if (this.legalPerson.id && this.legalPerson.promoters[index]) {
      this.confirmDialog('promoters', DialogAction.REMOVE, () => {
        this.splicePromoter(index);
        this.partialUpdate('promoters',  'general.api_legal_persons_promoters_successfully_removed');
      });
    } else {
      this.splicePromoter(index);
    }
  }
  splicePromoter(index: number): void {
    this.legalPerson.promoters.splice(index, 1);
    this.legalPersonPromoterIds.splice(index, 1);
    this.promoterScrollLoadList.splice(index, 1);
  }

  // EXTERNAL SHOP PROVIDERS
  private setShopProviders(): void {
    this.shopProviderService.getTotalElements()
      .pipe(
        switchMap(result => {
          const pageSize = result;
          return this.shopProviderService.search({ pageSize });
        })
      )
      .subscribe(result => {
        this.shopProviders = result.items;
        this.populateShopProviderScrollLoadList();
      });
  }

  getShopProvider(providerId: number): ShopProvider {
    return this.shopProviders.find(provider => provider.id === providerId);
  }
  populateShopProviderScrollLoadList(): void {
    this.shopProviderScrollLoadList = [];
    this.legalPersonShopProviders = [];
    if (!this.legalPerson.shopProviders) {
      this.legalPerson.shopProviders = [];
    }
    this.legalPerson.shopProviders.forEach((provider) => {
      const newScrollLoad = this.shopProviderService.getScrollLoad();
      newScrollLoad.object = this.getShopProvider(provider.id);
      this.shopProviderScrollLoadList.push(newScrollLoad);
      this.legalPersonShopProviders.push({...provider});
    });
  }
  selectExternalShopProvider(id: number, index: number): void {
    this.legalPersonShopProviders[index] = this.getSelectedShopProvider(index);
    this.editShopProviders[id] = true;
  }
  addExternalShopProvider(): void {
    this.legalPerson.shopProviders.unshift(null);
    this.shopProviderScrollLoadList.unshift(this.shopProviderService.getScrollLoad());
    this.legalPersonShopProviders.unshift(this.createEmptyProvider());
  }
  saveExternalShopProvider(id: number, index: number): void {
    this.editShopProviders[id] = false;
    const previousId = this.legalPerson.shopProviders[index]?.id;
    const newProvider = this.legalPersonShopProviders[index];

    if (!this.legalPerson.id) {
      this.legalPerson.shopProviders[index] = newProvider;
      this.pendingChanges = true;
    } else if (previousId) {
      this.handleUpdateShopProvider(previousId, newProvider, index);
    } else {
      this.handleAddShopProvider(newProvider, index);
    }
  }

  getSelectedShopProvider(index: number) {
    return this.shopProviderScrollLoadList[index]?.object;
  }
  removeExternalShopProvider(id: number, index: number): void {
    if (this.legalPerson.id && this.legalPerson.shopProviders[index]?.id) {
      this.confirmDialog('shop_providers', DialogAction.REMOVE, () => {
        this.handleDeleteShopProvider(id, index)
      });
    } else {
      this.spliceShopProvider(index);
    }
  }
  spliceShopProvider(index: number): void {
    this.legalPerson.shopProviders.splice(index, 1);
    this.shopProviderScrollLoadList.splice(index, 1);
    this.legalPersonShopProviders.splice(index, 1);
  }

  get attachedShopProviderIds(): number[] {
    return this.legalPersonShopProviders.filter(sp => sp.id).map(sp => sp.id);
  }

  // BO3 SHOP PROVIDERS
  addShopProvider(): void {
    const count = this.legalPerson.shopProviders.push(this.createEmptyProvider());
    this.openShopProviderDialog(count - 1);
  }
  editShopProvider(index: number): void {
    this.openShopProviderDialog(index, true);
  }
  removeShopProvider(providerId: number, index: number): void {
    if (this.legalPerson.id && this.legalPerson.shopProviders[index]) {
      this.confirmDialog('shop_providers', DialogAction.REMOVE, () => {
        this.handleDeleteShopProvider(providerId, index)
      });
    } else {
      this.legalPerson.shopProviders.splice(index, 1);
    }
  }
  openShopProviderDialog(index: number, edit?: boolean): void {
    const scope = this.legalPerson.shopProviders;
    const model = cloneDeep(scope[index]);
    const callback = (res: any) => {
      if (!res) return; // x btn click
      if (this.legalPerson.id) {
        edit ? this.handleUpdateShopProvider(res.id, res, index) : this.handleAddShopProvider(res, index);
      } else {
        scope[index] = res;
        this.pendingChanges = true;
      }
    };
    const isRequiredDataFilled = (): boolean => {
      return this.stringUtilService.isNotBlank(model.tag) && this.stringUtilService.isNotBlank(model.name);
    };
    this.openDialog({
      template: this.shopProviderModalContent,
      callback: callback,
      isValid: isRequiredDataFilled,
      validations: {},
      title: edit ? 'general.api_legal_persons_form_shop_provider_edit' : 'general.api_legal_persons_form_shop_provider_add',
      saveButton: edit ? 'general.api_legal_persons_form_contact_person_edit_button' : 'general.api_legal_persons_form_contact_person_add_button',
      model: model,
      index: index
    } as LegalPersonContentDialog);
  }
  handleAddShopProvider(provider: ShopProvider, index: number): void {
    this.legalPersonService.addShopProviders(this.legalPerson.id, [provider]).subscribe((savedProvider) => {
      this.legalPerson.shopProviders[index] = savedProvider[0];
      this.snackService.showSuccess(this.translateService.instant('general.api_legal_persons_shop_providers_successfully_saved'));
    });
  }
  handleUpdateShopProvider(shopProviderId: number, data: ShopProvider, index: number): void {
    this.legalPersonService.updateShopProvider(this.legalPerson.id, shopProviderId, data).subscribe((res) => {
      this.legalPerson.shopProviders[index] = res;
      this.snackService.showSuccess(this.translateService.instant('general.api_legal_persons_shop_providers_successfully_updated'));
    });
  }
  handleDeleteShopProvider(providerId: number, index: number) {
    this.legalPersonService.deleteShopProvider(this.legalPerson.id, providerId).subscribe(() => {
      this.snackService.showSuccess(this.translateService.instant('general.api_legal_persons_shop_providers_successfully_removed'));
      this.spliceShopProvider(index);
    })
  }

  createEmptyProvider(): ShopProvider {
    return {
      id: null,
      tag: null,
      name: null
    } as ShopProvider;
  }

  // SALESPOINTS
  private getSalespoints(): void {
    this.salespointsService.getSalesPoints({ superCentreLevel: true, hideUsed: true }).subscribe((salespoints) => {
      this.salespoints = salespoints;
      this.populateSalespointScrollLoadList();
    });
  }
  get getSelectedSalespointIds(): number[] {
    return this.legalPerson.salespoints.map((salespoint) => salespoint.id);
  }
  populateSalespointScrollLoadList(): void {
    this.salespointScrollLoadList = [];
    if (!this.legalPerson.salespoints) {
      this.legalPerson.salespoints = [];
    }
    this.legalPerson.salespoints.forEach((salespoint) => {
      const newScrollLoad = this.salespointsService.getScrollLoad();
      newScrollLoad.object = salespoint;
      this.salespointScrollLoadList.push(newScrollLoad);
    });
  }
  selectSalespoint(index: number): void {
    const salespoint = this.salespointScrollLoadList[index].object;
    if (salespoint) {
      this.legalPerson.salespoints[index].name = salespoint.name;
      this.legalPerson.salespoints[index].alias = salespoint.alias;
      this.editSalespoints[salespoint.id] = true;
    }
  }
  addSalespoint(): void {
    this.legalPerson.salespoints.unshift({
      id: null,
      name: null,
      alias: null,
      supportsYbo: true
    });
    this.salespointScrollLoadList.unshift(new ScrollLoad(50, 'id'));
  }

  addAnalyticsTracker(): void {
    this.openAnalyticsTrackerDialog(false, null);
  }
  updateAnalyticsTracker(index: number): void {
    this.openAnalyticsTrackerDialog(true, index);
  }
  removeAnalyticsTracker(index: number): void {
    if (this.legalPerson.analyticsTrackers[index]) {
      this.confirmDialog('analytics_trackers', DialogAction.REMOVE, () => {
        this.legalPerson.analyticsTrackers.splice(index, 1);
        this.partialUpdate('analyticsTrackers',  'general.api_legal_persons_analytics_tracker_successfully_removed');
      });
    }
  }
  openAnalyticsTrackerDialog(isUpdate: boolean, index?: number): void {
    const model = cloneDeep(this.getAnalyticsTrackerModel(index));

    const callback = (res: AnalyticsTrackerWrapper) => {
      if (res) {
        if (isUpdate) {
          this.legalPerson.analyticsTrackers[index].value = res.value;
        } else {
          this.legalPerson.analyticsTrackers.push(res);
        }

        if (this.legalPerson.id) {
          this.partialUpdate('analyticsTrackers',  'general.api_legal_persons_analytics_tracker_successfully_saved');
        } else {
          this.pendingChanges = true;
        }
      }
    };
    this.openDialog({
      template: this.analyticsTrackersTemplateModalContent,
      callback: callback,
      isValid: () => (isUpdate && model.value && model.value.length <= this.ANALYTICS_TRACKER_MAX_LENGTH)
        || (!isUpdate && model.type && model.value && model.value.length <= this.ANALYTICS_TRACKER_MAX_LENGTH),
      validations: {},
      title: isUpdate ? 'general.api_legal_persons_form_analytics_tracker_update' : 'general.api_legal_persons_form_analytics_tracker_add',
      saveButton: isUpdate ? 'general.api_legal_persons_form_analytics_tracker_update_button' : 'general.api_legal_persons_form_analytics_tracker_add_button',
      model: model,
      mode: isUpdate ? 'UPDATE' : 'ADD'
    } as LegalPersonContentDialog);
  }

  private getAnalyticsTrackerModel(index: number): AnalyticsTracker {
    if (index == null) {
      return { type: null, value: null };
    }
    return this.legalPerson.analyticsTrackers[index];
  }

  saveSalespoint(id: number): void {
    this.editSalespoints[id] = false
    this.partialUpdate('salespoints',  'general.api_legal_persons_salespoints_successfully_saved');
  }
  removeSalespoint(index: number): void {
    if (this.legalPerson.id && this.legalPerson.salespoints[index].id) {
      this.confirmDialog('salespoints', DialogAction.REMOVE, () => {
        this.spliceSalespoint(index);
        this.partialUpdate('salespoints',  'general.api_legal_persons_salespoints_successfully_removed');
      });
    } else {
      this.spliceSalespoint(index);
    }
  }
  spliceSalespoint(index: number): void {
    this.legalPerson.salespoints.splice(index, 1);
    this.salespointScrollLoadList.splice(index, 1);
    if (!this.legalPerson.salespoints?.length && (this.legalPerson.promoters?.length)) {
      this.legalPerson.promoters = [];
      this.legalPersonPromoterIds = [];
    }
  }

  // CONFIRM DIALOG
  confirmDialog(name: string, action: string, callback: Function): void {
    const confirm = this.dialog.open(ConfirmDialogComponent, {
      width: '60%',
      panelClass: 'xs-popup',
      data: {
        title: this.translateService.instant('general.api_legal_persons_' + name + '_confirm_' + action + '_title'),
        description: this.translateService.instant('general.api_legal_persons_' + name + '_confirm_' + action + '_description'),
        buttonCancel: this.translateService.instant('general.api_general_no'),
        buttonOk: this.translateService.instant('general.api_general_yes')
      }
    });
    confirm.afterClosed().subscribe((res) => {
      if (res) {
        callback();
      }
    });
  }

  // PROJECT MANAGERS
  populateProjectManagers(): void {
    const data: UserGet = {
      filters: {
        privilege: Privileges.LEGAL_PERSON_PROJECT_MANAGER,
        status: UserStatus.Active
      },
      page: 1,
      pageSize: this.projectManagersPageSize,
      sortOrder: SortOrderEnum.Asc
    };
    this.userService.search(data).subscribe((res) => this.projectManagers = res.items);
  }

  saveProjectManager(): void {
    this.partialUpdate('projectManagerId',  'general.api_legal_persons_project_manager_successfully_saved');
  }

  get selectedProjectManager(): User {
    return this.legalPerson.projectManagerId && find(this.projectManagers, { userId: this.legalPerson.projectManagerId }) || null;
  }

  // CONTACT PERSONS
  get getSelectedContactPersonEmails(): string[] {
    return this.legalPerson.contactPersons.map((contact) => contact.email);
  }

  get representativesCount(): number {
    return (this.legalPerson.representatives || []).filter(v => !!v.userId).length;
  }

  addContactPerson(): void {
    const count = this.legalPerson.contactPersons.push({
      id: null,
      firstName: null,
      lastName: null,
      email: null,
      phoneNr: null,
      remarks: null,
      isPublic: false
    });
    this.openContactPersonDialog(count - 1);
  }

  editContactPerson(contactIndex: number): void {
    this.openContactPersonDialog(contactIndex, true);
  }

  openContactPersonDialog(index: number, edit?: boolean): void {
    const scope = this.legalPerson.contactPersons;
    const model = cloneDeep(scope[index]);
    const callback = (res: any) => {
      if (res) {
        if (this.legalPerson.id) {
          if (edit) {
            this.handleUpdateContactPerson(res, index)
          } else {
            this.handleAddContactPerson(res, index);
          }
        } else {
          scope[index] = res;
          this.pendingChanges = true;
        }
      } else if (!model.id) {
        this.spliceContactPerson(index);
      }
    };
    const isEmailValid = () => {
      return !model.email || ValidationService.validateEmail(model.email);
    };
    const isEmailExist = () => {
      return model.email && !!scope.filter((cp, cpIndex) => cpIndex !== index && cp.email === model.email).length;
    };
    const isValid = () => {
      return model.id || (model.firstName && ValidationService.validateString(model.firstName, 2, CONTACT_PERSON_NAME_MAX_LENGTH) &&
        model.lastName && ValidationService.validateString(model.lastName, 2, CONTACT_PERSON_NAME_MAX_LENGTH) &&
        (!model.phoneNr || ValidationService.validatePhone(model.phoneNr)) && isEmailValid() && !isEmailExist());
    };
    this.openDialog({
      template: this.contactPersonModalContent,
      callback: callback,
      isValid: isValid,
      validations: {isEmailValid, isEmailExist},
      title: edit ? 'general.api_legal_persons_form_contact_person_edit' : 'general.api_legal_persons_form_contact_person_add',
      saveButton: edit ? 'general.api_legal_persons_form_contact_person_edit_button' : 'general.api_legal_persons_form_contact_person_add_button',
      model: model,
      index: index,
      nameMaxLength: CONTACT_PERSON_NAME_MAX_LENGTH
    } as LegalPersonContentDialog);
  }

  handleAddContactPerson(contactPerson: ContactPerson, index: number): void {
    this.legalPersonService.addContactPersons(this.legalPerson.id, [contactPerson]).pipe(
      tap({error: () => this.spliceContactPerson(index)})
    ).subscribe((contactPersons) => {
      this.legalPerson.contactPersons[index] = contactPersons[0];
      this.sortContactPersons();
      this.syncModels();
      this.snackService.showSuccess(this.translateService.instant('general.api_contact_person_successfully_added'));
    });
  }

  handleUpdateContactPerson(contactPerson: ContactPerson, index: number): void {
    this.legalPersonService.updateContactPerson(this.legalPerson.id, contactPerson).subscribe((res) => {
      this.legalPerson.contactPersons[index] = res;
      this.sortContactPersons();
      this.syncModels();
      this.snackService.showSuccess(this.translateService.instant('general.api_contact_person_successfully_updated'));
    });
  }

  removeContactPerson(index: number): void {
    if (this.legalPerson.id && this.legalPerson.contactPersons[index].id) {
      const confirm = this.dialog.open(ConfirmDialogComponent, {
        width: '60%',
        panelClass: 'xs-popup',
        data: {
          title: this.translateService.instant('general.api_contact_person_confirm_remove_title'),
          description: this.translateService.instant('general.api_contact_person_confirm_remove_description'),
          buttonCancel: this.translateService.instant('general.api_general_no'),
          buttonOk: this.translateService.instant('general.api_general_yes')
        }
      });
      confirm.afterClosed().subscribe((res) => {
        if (res) {
          this.legalPersonService.removeContactPerson(this.legalPerson.id, this.legalPerson.contactPersons[index].id).subscribe(() => {
            this.spliceContactPerson(index);
            this.snackService.showSuccess(this.translateService.instant('general.api_contact_person_successfully_removed'));
          });
        }
      });
    } else {
      this.spliceContactPerson(index);
      this.pendingChanges = true;
    }
  }

  spliceContactPerson(index: number): void {
    this.legalPerson.contactPersons.splice(index, 1);
    this.sortContactPersons();
    this.syncModels();
  }

  // REPRESENTATIVES
  getRepresentativeRoles(roles: RoleSimple[]): string {
    return roles.map(role => role.name).join(', ');
  }

  addRepresentative(): void {
    if (!this.legalPerson.salespoints?.length) {
      this.snackService.showError(this.translateService.instant('general.api_representatives_add_salespoint_first'));
      return;
    }
    if (this.isSalespointsChanged) {
      this.snackService.showError(this.translateService.instant('general.api_representatives_add_salespoint_unsaved_changes'));
      return;
    }
    const count = this.legalPerson.representatives.push({
      salespointRoles: [{
        id: !this.legalHasMultipleSalespoints ? this.legalPerson.salespoints[0].id : null,
        roles: []
      }]
    });
    this.syncModels();
    this.openRepresentativeDialog(count - 1);
  }

  editRepresentative(index: number): void {
    this.openRepresentativeDialog(index, true);
  }

  openRepresentativeDialog(index: number, edit?: boolean): void {
    const scope = this.legalPerson.representatives;
    const scopeInvites = this.legalPerson.representativeInvites;
    const model: Representative = cloneDeep(scope[index]);
    const callback = (res: any) => {
      if (res) {
        if (this.legalPerson.id) {
          if (edit) {
            this.handleUpdateRepresentative(res, index);
          } else {
            this.handleAddRepresentative(res, index);
          }
        } else {
          this.pendingChanges = true;
        }
      } else if (!model.userId) {
        this.spliceRepresentative(index);
      }
    };
    const secondaryAction = () => {
      model.salespointRoles.push({
        roles: []
      });
    };
    const secondaryButtonDisabled = () => {
      return !this.legalPerson.salespoints?.length || model.salespointRoles.filter(sp => !this.isAliasSalespoint(sp.id)).length === this.legalPerson.salespoints.length;
    };
    const removeAction = (index: number) => {
      const id = cloneDeep(model.salespointRoles[index]).id;
      model.salespointRoles.splice(index, 1);
      if (id && this.aliasSalespoint(id)) {
        const aliasIndex = model.salespointRoles.map(sp => sp.id).indexOf(this.aliasSalespoint(id).id);
        if (aliasIndex !== -1) {
          model.salespointRoles.splice(aliasIndex, 1);
        }
      }
    };
    const isEmailValid = () => {
      return !model.email || ValidationService.validateEmail(model.email);
    };
    const isEmailExist = () => {
      return model.email && !![scope, scopeInvites].filter((s: any[]) => !!s.filter((i: any, idx: number) => idx !== index && i.email === model.email).length).length;
    };
    const isValid = () => {
      return model && model.email && isEmailValid() && !isEmailExist() && model.salespointRoles?.length &&
        model.salespointRoles?.filter((sp: any) => sp.id && sp.roles?.length && (!this.aliasSalespoint(sp.id) || sp.aliasRoles?.length)).length === model.salespointRoles?.length;
    };
    this.openDialog({
      template: this.representativesModalContent,
      callback: callback,
      isValid: isValid,
      validations: {isEmailValid, isEmailExist},
      title: edit ? 'general.api_legal_persons_form_representative_edit' :'general.api_legal_persons_form_representative_add',
      titleTooltip: edit ? 'general.api_legal_persons_form_representative_edit_tooltip' : 'general.api_legal_persons_form_representative_add_tooltip',
      saveButton: edit ? 'general.api_legal_persons_form_representative_edit_button' : 'general.api_legal_persons_form_representative_add_button',
      secondaryButton: this.legalHasMultipleSalespoints ? 'general.api_legal_persons_form_representative_salespoint_button' : null,
      secondaryAction: secondaryAction,
      removeAction: removeAction,
      secondaryButtonDisabled: secondaryButtonDisabled,
      width: this.legalHasMultipleSalespoints || this.legalHasAliasSalespoints || edit ? '70%': '40%',
      model: model,
      index: index
    } as LegalPersonContentDialog);
  }

  prepareAliasRoles(roles: SalespointRole[]): SalespointRole[] {
    roles.forEach((sp: any) => {
      const alias = this.aliasSalespoint(sp.id);
      if (alias) {
        const aliasRoles = cloneDeep(sp.aliasRoles);
        const aliasSp = find(roles, { id: alias.id });
        if (aliasSp) {
          aliasSp.roles = aliasRoles;
        } else {
          roles.push({
            id: alias.id,
            roles: aliasRoles
          })
        }
      }
    });
    return roles;
  }

  handleAddRepresentative(representative: Representative, index: number): void {
    const data: RepresentativeInvitePost = { email: representative.email, salespointRoles: this.prepareAliasRoles(representative.salespointRoles) };
    this.legalPersonService.addRepresentative(this.legalPerson.id, data).pipe(
      tap({error: () => this.spliceRepresentative(index)})
    ).subscribe((res) => {
      this.spliceRepresentative(index);
      this.legalPerson.representativeInvites.push(res);
      this.syncModels();
      this.switchRepresentativeTab(this.representativeTabMap.INVITES);
      this.snackService.showSuccess(this.translateService.instant('general.api_representative_successfully_added'));
    });
  }

  handleUpdateRepresentative(representative: Representative, index: number): void {
    this.legalPersonService.updateRepresentative(this.legalPerson.id, representative.userId, this.prepareAliasRoles(representative.salespointRoles)).subscribe((res) => {
      this.legalPerson.representatives[index] = res;
      this.sortRepresentatives();
      this.syncModels();
      this.snackService.showSuccess(this.translateService.instant('general.api_representative_successfully_updated'));
    });
  }

  removeRepresentative(index: number): void {
    if (this.legalPerson.id && this.legalPerson.representatives[index].userId) {
      const confirm = this.dialog.open(ConfirmDialogComponent, {
        width: '60%',
        panelClass: 'xs-popup',
        data: {
          title: this.translateService.instant('general.api_representative_confirm_remove_title'),
          description: this.translateService.instant('general.api_representative_confirm_remove_description'),
          buttonCancel: this.translateService.instant('general.api_general_no'),
          buttonOk: this.translateService.instant('general.api_general_yes')
        }
      });
      confirm.afterClosed().subscribe((res) => {
        if (res) {
          this.legalPersonService.removeRepresentative(this.legalPerson.id, this.legalPerson.representatives[index].userId).subscribe(() => {
            this.snackService.showSuccess(this.translateService.instant('general.api_representative_successfully_removed'));
            this.spliceRepresentative(index);
          });
        }
      });
    }
  }

  spliceRepresentative(index: number): void {
    this.legalPerson.representatives.splice(index, 1);
    this.syncModels();
  }

  // INVITES
  resendInvite(index: number, callback?: Function): void {
    if (this.legalPerson.representativeInvites[index].id) {
      this.legalPersonService.resendInvite(this.legalPerson.id, this.legalPerson.representativeInvites[index].id).subscribe(() => {
        this.legalPerson.representativeInvites[index].inviteStatus = this.inviteStatus.Pending;
        this.syncModels();
        this.snackService.showSuccess(this.translateService.instant('general.api_representative_invite_successfully_resent'));
        if (callback) {
          callback();
        }
      });
    }
  }

  editInvite(index: number): void {
    const scope = this.legalPerson.representativeInvites;
    const model = cloneDeep(scope[index]);
    const callback = (res: any) => {
      if (res) {
        if (this.legalPerson.id) {
          this.handleUpdateInvite(res, index);
        } else {
          this.pendingChanges = true;
        }
      }
    };
    const secondaryAction = (data?: any) => {
      this.resendInvite(index, data.closeModal);
    };
    const secondaryButtonDisabled = () => {
      return false;
    };
    const isValid = () => {
      return model && model.salespointRoles?.length &&
        model.salespointRoles?.filter((sp: any) => sp.id && sp.roles?.length && (!this.aliasSalespoint(sp.id) || sp.aliasRoles?.length)).length === model.salespointRoles?.length;
    };
    this.openDialog({
      template: this.representativesModalContent,
      callback: callback,
      isValid: isValid,
      title: 'general.api_legal_persons_form_representative_invite_edit',
      titleTooltip: 'general.api_legal_persons_form_representative_invite_edit_tooltip',
      saveButton: 'general.api_legal_persons_form_representative_invite_edit_button',
      secondaryButton: 'general.api_legal_persons_form_representative_invite_resend_button',
      secondaryAction: secondaryAction,
      secondaryButtonDisabled: secondaryButtonDisabled,
      width: '70%',
      model: model,
      index: index
    } as LegalPersonContentDialog);
  }

  handleUpdateInvite(invite: RepresentativeInvite, index: number): void {
    const data: RepresentativeInvitePost = { email: invite.email, salespointRoles: this.prepareAliasRoles(invite.salespointRoles) };
    this.legalPersonService.updateInvite(this.legalPerson.id, invite.id, data).subscribe((res) => {
      this.legalPerson.representativeInvites[index] = res;
      this.syncModels();
      this.snackService.showSuccess(this.translateService.instant('general.api_representative_invite_successfully_updated'));
    });
  }

  removeInvite(index: number): void {
    if (this.legalPerson.id && this.legalPerson.representativeInvites[index].id) {
      const confirm = this.dialog.open(ConfirmDialogComponent, {
        width: '60%',
        panelClass: 'xs-popup',
        data: {
          title: this.translateService.instant('general.api_representative_invite_confirm_remove_title'),
          description: this.translateService.instant('general.api_representative_invite_confirm_remove_description'),
          buttonCancel: this.translateService.instant('general.api_general_no'),
          buttonOk: this.translateService.instant('general.api_general_yes')
        }
      });
      confirm.afterClosed().subscribe((res) => {
        if (res) {
          this.legalPersonService.removeInvite(this.legalPerson.id, this.legalPerson.representativeInvites[index].id).subscribe(() => {
            this.snackService.showSuccess(this.translateService.instant('general.api_representative_invite_successfully_removed'));
            this.spliceInvite(index);
            if (!this.legalPerson.representativeInvites.length) {
              this.switchRepresentativeTab(this.representativeTabMap.VERIFIED);
            }
          });
        }
      });
    }
  }

  spliceInvite(index: number): void {
    this.legalPerson.representativeInvites.splice(index, 1);
    this.syncModels();
  }

  unsetPartialComponentState(): void {
    this.editSalespoints = { null: true };
    this.editPromoters = { null: true };
    this.editShopProviders = { null: true };
  }

  // REGISTER
  register(doAuthAction?: boolean): void {
    this.create(true, doAuthAction);
  }

  // CREATE
  create(useRegistrationFlow?: boolean, doAuthAction?: boolean): void {
    const invoicing = cloneDeep(this.legalPerson.invoicingPeriod);
    const contactPersons = this.getContactPersons();
    const shopProviders = this.getUnsavedShopProviders();
    this.legalPersonService.add(this.getData())
    .subscribe(
      {
        next: (legalPerson) => {
          if (useRegistrationFlow) {
            this.isLoading = true;
          }
          this.legalPerson = legalPerson;
          this.legalPersonOrig = cloneDeep(legalPerson);
          this.snackService.showSuccess(this.translateService.instant('general.api_legal_persons_successfully_created'));
          if (invoicing) {
            this.updateInvoicingPeriod(invoicing, true);
          }
          if (contactPersons?.withoutId?.length) {
            this.legalPersonService.addContactPersons(this.legalPerson.id, contactPersons.withoutId).subscribe((res) => {
              this.legalPerson.contactPersons = res;
              this.sortContactPersons();
              this.syncModels();
            });
          }
          if (shopProviders?.length) {
            this.legalPersonService.addShopProviders(this.legalPerson.id, shopProviders).subscribe(res => {
              this.legalPerson.shopProviders = res;
            })
          }
          if (this.isInternalComponent) {
            this.confirmChange.emit(this.legalPerson);
          }
          this.unsetPartialComponentState();
          this.pendingChanges = false;
          this._errors = {};
          if (!this.isInternalComponent) {
            if (doAuthAction) {
              this.authService.pendingChangeAction();
            } else {
              if (!this.isInternalComponent) {
                if (useRegistrationFlow) {
                  this.claimLegal(true);
                } else {
                  this.routeService.replaceUrl('/app/admin/legal-persons/view/' + this.legalPerson.id);
                  this.updatePermissions();
                  this.getAdditionalData();
                }
              }
            }
          }
        },
        error: (e) => {
          if (useRegistrationFlow && e?.error?.message && e.error.message === 'api_legal_person_unclaimed_duplicate_reg_nr') {
            this.confirmDialog('unclaimed_legal', DialogAction.CLAIMING, () => {
              this.legalPerson.id = e.error.returnValue;
              this.pendingChanges = false;
              this.update(doAuthAction, useRegistrationFlow);
            });
          } else if (e?.error?.message && (e.error.message === 'api_legal_person_duplicate_reg_nr' || e.error.message === 'api_legal_person_unclaimed_duplicate_reg_nr')) {
            this._errors.regNumberExist = cloneDeep(this.legalPerson.regNumber);
            console.log(this._errors);
            this.snackService.showBackendError(e);
            this.isLoading = false;
          }
        }
      });
  }

  confirmClaimLegal() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '60%',
      panelClass: 'xs-popup',
      data: {
        title: this.translateService.instant('general.api_legal_persons_claim_legal_confirmation_title'),
        description: this.translateService.instant('general.api_legal_persons_claim_legal_confirmation_description'),
        buttonCancel: this.translateService.instant('general.api_legal_persons_claim_legal_confirmation__button_cancel'),
        buttonOk: this.translateService.instant('general.api_legal_persons_claim_legal_confirmation_title_button_ok')
      }
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.claimLegal();
      }
    });
  }

  private claimLegal(registrationFlow?: boolean) {
    this.authService.getPermissions().subscribe(() => {
      this.legalPersonService.claimLegal(this.legalPerson?.id).subscribe((legalPerson) => {
        if (!registrationFlow) {
          // Don't show for registration flow since navigation happens right after.
          this.snackService.showSuccess(this.translateService.instant('general.api_legal_persons_legal_claim_success'));
        }
        this.legalPerson = legalPerson;
        this.postLegalPersonsUpdate();
        this.setIsBusinessAccount();
        if (registrationFlow) {
          this.isLoading = false;
          this.navigateOnRegistration();
        } else {
          this.getRoles();
        }
      })
    })
  }

  navigateOnRegistration(): void {
    this.authService.getPermissions().subscribe(() => {
      const permission = find(this.authService.permissions, { legalPerson: { id: this.legalPerson.id }});
      if (permission) {
        this.authService.changeRole(permission.salespointId, permission.role.id).subscribe(() => {
          this.routeService.navigate('/app/admin/legal-persons/view/' + permission.legalPerson.id);
        });
      } else {
        this.authService.navigateToBaseUrl();
      }
    });
  }

  // UPDATE
  update(doAuthAction?: boolean, claimLegalAfterUpdate?: boolean): void {
    const invoicing = cloneDeep(this.legalPerson.invoicingPeriod);
    this.legalPersonService.update(this.legalPerson.id, this.getData()).pipe(
      tap({error: (e) => {
        if (e?.error?.message && e.error.message === 'api_legal_person_duplicate_reg_nr') {
          this._errors.regNumberExist = cloneDeep(this.legalPerson.regNumber);
        }
      }})
    ).subscribe((legalPerson) => {
      if (claimLegalAfterUpdate) {
        this.isLoading = true;
      }
      this.snackService.showSuccess(this.translateService.instant('general.api_legal_persons_successfully_updated'));
      if (this.invoicingChanged) {
        this.updateInvoicingPeriod(invoicing, true, legalPerson);
      } else {
        this.legalPerson = legalPerson;
        this.postLegalPersonsUpdate();
      }
      if (!this.isInternalComponent) {
        this.confirmChange.emit(this.legalPerson);
      }
      this.updatePermissions();
      this.getAuditLogs();
      this.getAdditionalData();
      this.unsetPartialComponentState();
      if (doAuthAction) {
        this.authService.pendingChangeAction();
      } else {
        if (this.pendingChanges && this.authService.salespoint?.legalPerson.id === this.legalPerson?.id) {
          this.authService.refreshPermissions();
        }
        if (claimLegalAfterUpdate) {
          this.claimLegal(claimLegalAfterUpdate)
        } else {
          this._errors = {};
          if (this.isRegister) {
            this.navigateOnRegistration();
          } else if (!this.isView) {
            this.routeService.navigate('/app/admin/legal-persons/view/' + this.legalPerson.id)
          }
        }
      }
      this.pendingChanges = false;
    });
  }

  partialUpdate(scope: string, message: string): void {
    const orig = cloneDeep(this.legalPersonOrig);
    // @ts-ignore
    orig[scope] = this.legalPerson[scope];
    this.legalPersonService.update(this.legalPerson.id, this.getData(orig)).subscribe({
      next: (res) => {
        // @ts-ignore
        this.legalPerson[scope] = res[scope];
        // @ts-ignore
        this.legalPersonOrig[scope] = cloneDeep(res[scope]);

        this.updatePermissions();
        if (scope === 'salespoints') {
          this.getRoles();
          this.setIsBusinessAccount();
          this.getSalespoints();
        }
        this.snackService.showSuccess(this.translateService.instant(message));
      },
      error: () => {
        // @ts-ignore
        const origModel = cloneDeep(this.legalPersonOrig);
        // @ts-ignore
        this.legalPerson[scope] = origModel[scope];
        if (scope === 'salespoints') {
          this.populateSalespointScrollLoadList();
        }
        if (['salespoints', 'promoters'].includes(scope) && origModel.promoters?.length) {
          this.legalPerson.promoters = origModel.promoters;
          this.legalPersonPromoterIds = origModel.promoters;
          this.populatePromoterScrollLoadList();
        }
        if (scope === 'shopProviders') {
          this.populateShopProviderScrollLoadList();
        }
      }
    });
  }

  updateInvoicingPeriod(value: InvoicingPeriod, saveInBackground?: boolean, legalPersonObj?: LegalPerson): void {
    if (!this.authService.checkPrivileges(Privileges.LEGAL_PERSON_ADMIN)) {
      return;
    }
    this.legalPersonService.updateInvoicingPeriod(this.legalPerson.id, { selectedPeriod: value }).subscribe({
      next: (legalPerson) => {
        this.legalPerson = legalPerson;
        this.postLegalPersonsUpdate();
        this.invoicingChanged = false;
        if (!saveInBackground) {
          this.snackService.showSuccess(this.translateService.instant('general.api_legal_persons_invoicing_period_updated'))
        }
      },
      error: () => {
        this.legalPerson = legalPersonObj;
        this.postLegalPersonsUpdate();
      }
    });
  }

  billingGetBankAccounts(): void {
    if (this.canChange && this.authService.checkPrivileges(Privileges.LEGAL_PERSON_BANK_ACCOUNT_VIEW)) {
      if (this.authService.user.superCentreId === LegalPersonViewComponent.BELARUS_SUPER_CENTRE_ID) { // restricted view for Belarus super centre
        this.BankAccountStatuses = this.BankAccountStatuses.filter(status => status === BankAccountStatus.Active || status === BankAccountStatus.Archived);
      }
      this.legalPersonService.getBankAccounts(this.legalPerson.id).subscribe((res) => {
        this.billingBankAccounts = res;
      });
    }
  }

  billingAddBankAccount(): void {
    if (this.authService.checkPrivileges(Privileges.LEGAL_PERSON_BANK_ACCOUNT_ADD_VIA_PAYMENT_LINK)) {
      this.billingAddAccountWithPaymentLink();
    } else if (this.authService.checkPrivileges(Privileges.LEGAL_PERSON_ADMIN)) {
      this.billingAddManually();
    }
  }

  billingAddAccountWithPaymentLink(): void {
    this.generatePaymentLink = false;
    let _isLoading: boolean = false;
    let _model: PaymentLink = null;
    this.isBankAccountPending = !!this.billingBankAccounts.find(account => account.status === BankAccountStatus.Pending);
    const fetchNewPaymentLink = (): void => {
      if(this.legalPerson.id && !this.isBankAccountPending) {
        this.generatePaymentLink = true;
        _isLoading = true;
        this.legalPersonService.getBankAccountPaymentLink(this.legalPerson.id).subscribe((paymentLink) => {
          _model = paymentLink;
          _isLoading = false;
        });
      }
    }
    const model = (): any => {
      return _model;
    };
    const isLoading = (): boolean => {
      return _isLoading;
    };
    const primaryButton = this.authService.checkPrivileges(Privileges.LEGAL_PERSON_ADMIN) && 'general.api_legal_persons_form_billing_add_manually';
    const primaryAction = (data?: any) => {
      this.billingAddManually();
      data.closeModal();
    };
    const secondaryAction = () => {
      navigator.clipboard.writeText(_model.link).then(() => {
        this.snackService.showSuccess(this.translateService.instant('general.api_legal_persons_form_billing_payment_link_copied'));
      }).catch(() => {
        this.snackService.showError(this.translateService.instant('general.api_legal_persons_form_billing_payment_link_copy_failed'));
      });
    };

    this.openDialog({
      template: this.billingPaymentLinkModalContent,
      title: 'general.api_legal_persons_form_billing_payment_link',
      primaryButton: primaryButton,
      primaryAction: primaryAction,
      secondaryAction: secondaryAction,
      model: model,
      isLoading: isLoading,
      additionalAction: fetchNewPaymentLink
    } as LegalPersonContentDialog);
  }

  billingAddManually(): void {
    const model = cloneDeep({ iban: '' });
    const callback = (res: any) => {
      if (res) {
        this.confirmAndAddManually(res.iban);
      }
    };
    const isInvalid = () => {
      return !!model.iban?.length && !this.utilService.isValidIBAN(model.iban);
    };
    const isValid = () => {
      return !model.iban?.length || this.utilService.isValidIBAN(model.iban);
    }
    this.openDialog({
      template: this.billingAddManuallyModalContent,
      callback: callback,
      validations: {isInvalid},
      isValid: isValid,
      title: 'general.api_legal_persons_form_billing_add_manually_title',
      saveButton: 'general.api_legal_persons_form_billing_add_manually_button',
      model: model
    } as LegalPersonContentDialog);
  }

  confirmAndAddManually(iban: string): void {
    this.confirmDialog('billing', DialogAction.ADD_MANUALLY, () => {
      this.legalPersonService.addBankAccountManually(this.legalPerson.id, iban).subscribe((bankAccount) => {
        this.snackService.showSuccess(this.translateService.instant('general.api_legal_persons_billing_bank_account_added', { iban: bankAccount.iban }));
        this.billingGetBankAccounts();
        this.authService.user.isLegalBankAccountVerified = true;
        this.snackService.clearFixedSnackMessage();
      });
    });
  }

  syncModels(): void {
    if (!this.legalPerson.representatives) {
      this.legalPerson.representatives = [];
    }
    this.parseAliasRoles(this.legalPerson.representatives);
    if (!this.legalPerson.representativeInvites) {
      this.legalPerson.representativeInvites = [];
    }
    this.parseAliasRoles(this.legalPerson.representativeInvites);
    this.legalPersonOrig = cloneDeep(this.legalPerson);
  }

  parseAliasRoles(scope: any): void {
    scope.forEach((item: Representative | RepresentativeInvite) => {
      item.salespointRoles.forEach((sp: any) => {
        const alias = this.aliasSalespoint(sp.id);
        if (alias && alias.id) {
          sp.aliasRoles = find(item.salespointRoles, { id: alias.id })?.roles || [];
        }
      });
    });
  }

  checkVatCodeForChanges(): void {
    if (!this.isLiableForVat && (this.legalPerson.vatCode || this.legalPerson.vatPercent)) {
      this.pendingChanges = true;
      this.legalPerson.vatCode = null;
      this.legalPerson.vatPercent = null;
    } else {
      if (this.legalPersonOrig.vatCode) {
        this.legalPerson.vatCode = this.legalPersonOrig.vatCode;
      }
      if (this.legalPersonOrig.vatPercent) {
        this.legalPerson.vatPercent = this.legalPersonOrig.vatPercent;
      }
    }
  }
  public checkExemptCodeForChanges(): void {
    if (!this.isExemptFromVat && this.legalPerson.vatExemptionCode) {
      this.pendingChanges = true;
      this.legalPerson.vatExemptionCode = null;
    } else {
      if (this.legalPersonOrig.vatExemptionCode) {
        this.legalPerson.vatExemptionCode = this.legalPersonOrig.vatExemptionCode;
      }
    }
  }

  postLegalPersonsUpdate(): void {
    this.sortContactPersons();
    this.sortRepresentatives();
    this.syncModels();
    this.setIsBusinessAccount();
  }

  toggleRow(row: string): void {
    this.expanded[row] = !this.expanded[row];
  }

  cancel(): void {
    this.cancelChange.emit();
  }

  back(): void {
    this.routeService.navigate('/app/admin/legal-persons');
  }

  continueWithoutLegal(): void {
    this.authService.navigateToBaseUrl();
  }

  continueWithoutSaving(): void {
    this.pendingChanges = false;
    this.invoicingChanged = false;
    this.syncModels();
  }

  saveAndContinue(doAuthAction?: boolean): void {
    if (this.isRegister) {
      this.syncModels();
      this.register(doAuthAction);
    }
    if (this.isCreate) {
      this.create(false, doAuthAction);
    }
    if (this.isView) {
      this.update(doAuthAction);
    }
  }

  get pendingChangesDescription(): string {
    return this.translateService.instant('general.api_legal_persons_description_unsaved_changes');
  }

  public getAuditLogs(): void {
    if (this.canSeeAuditLogs && !this.isInternalComponent) {
      this.legalPersonService.getAuditLogs(this.legalPerson.id, this.auditPageNumber, this.auditPageSize).subscribe((auditPage: AuditLogPage) => {
        this.auditLogPage = auditPage;
      });
    }
  }

  public get canSeeAuditLogs(): boolean {
    return this.authService.hasAuditGetPrivilege &&
      (this.authService.user.isSuperCentre || this.legalPerson.id === this.authService.salespoint.legalPerson.id);
  }

  public get canSeeCommissionFees(): boolean {
    return !this.isCreate && (this.authService.user.isSuperCentre || this.legalPerson.id === this.authService.salespoint.legalPerson.id);
  }

  public get supportsYbo():boolean {
    return this.legalPerson.salespoints && this.legalPerson.salespoints.some(sp => sp.supportsYbo);
  }

  public get hasShopProviders(): boolean {
    return this.legalPerson.shopProviders.length > 0;
  }

  public isVatExemptCodeInvalid(code: string): boolean {
    return !code?.match('.+EX$');
  }

  public switchRepresentativeTab(tab?: RepresentativeTab): void {
    // If tab provided, switch and update active index
    if (tab) {
      this.representativeTab = tab;
      this.representativeTabIndex = this.representativeTabsList.indexOf(tab);
    // Otherwise set new tab based on the active index
    } else {
      const newTab = this.representativeTabsList[this.representativeTabIndex];
      if (newTab) {
        this.representativeTab = newTab;
      }
    }
  }

  protected readonly EVENT_TITLE_MAX_LENGTH = EVENT_TITLE_MAX_LENGTH;
  protected readonly CommissionLevel: {SuperCentre: CommissionLevel, Legal: CommissionLevel, Event: CommissionLevel} = CommissionLevel;
  protected readonly TextInputType = TextInputType;
  protected readonly NumericInputType = NumericInputType;
  protected readonly S3UploaderRatios = S3UploaderRatios;
  protected readonly S3UploaderTypes = S3UploaderTypes;
  protected readonly CONTACT_PERSON_NAME_MAX_LENGTH = CONTACT_PERSON_NAME_MAX_LENGTH;
}
